
// import '@google/model-viewer-effects'; // just for bundling
import '@google/model-viewer'; // just for bundling

import { EffectComposer } from '@google/model-viewer-effects';

/* super-outline-effect Playground

Adam Wolf

*/

import { Effect, BlendFunction, EffectAttribute } from "postprocessing";

import { Uniform, Color } from "three";



// class DebuggingEffect extends Effect {
//   constructor(name, fragmentShader, options = {}) {
//     console.log('Effect constructor about to be called with name:', name);
//     debugger;
//     super(name, fragmentShader, options);
//     console.log('Effect constructor finished for:', name);
//   }
// }

class SuperOutlineEffect extends Effect {
  constructor({
    blendFunction = BlendFunction.NORMAL,
    showMaterial = 1.0,
    showMaterialLuma = 0.0,
    showNormalMap = 0.0,
    showDepthMap = 0.0,
    outlineEnabled = 1.0,
    outlineThickness = 1.0,
    outlineColor = new Color(0x000000),
    depthCoefficient = 10.0,
    normalCoefficient = 10.0,
    lumaCoefficient = 2.5,
    shakyAmplitude = 2.0,
    shakyFrequency = 0.08,
    shakyEnabled = 0.0,
  } = {}) {
    const fragmentShader = `
    uniform sampler2D normalBuffer;

    uniform float threshold;

    uniform float showNormalMap;
    uniform float showDepthMap;
    uniform float showMaterial;
    uniform float showMaterialLuma;

    uniform float outlineEnabled;
    uniform float outlineThickness;
    uniform vec3 outlineColor;
    uniform float depthCoefficient;
    uniform float normalCoefficient;
    uniform float lumaCoefficient;
    
    uniform float shakyEnabled;
    uniform float shakyAmplitude;
    uniform float shakyFrequency;
    
    const mat3 Sx = mat3( -1, -2, -1, 0, 0, 0, 1, 2, 1 );
    const mat3 Sy = mat3( -1, 0, 1, -2, 0, 2, -1, 0, 1 );


    float hash(vec2 p) {
      vec3 p3  = fract(vec3(p.xyx) * .1031);
      p3 += dot(p3, p3.yzx + 33.33);
      return fract((p3.x + p3.y) * p3.z);
    }

    float orthographicDepth(vec2 uv) {
        float d = readDepth(uv);
        float viewZ = getViewZ(d);
        float pd = viewZToOrthographicDepth(viewZ, cameraNear, cameraFar);
        return pd;
    }

    void mainImage(const in vec4 inputColor, const in vec2 uv, const in float depth, out vec4 outputColor) {
        vec2 fragCoord = uv * resolution;

        vec2 displacement = vec2(
          (hash(fragCoord.xy) * sin(fragCoord.y * shakyFrequency)) ,
          (hash(fragCoord.xy) * cos(fragCoord.x * shakyFrequency))
        ) * shakyAmplitude /resolution.xy;

        displacement *= shakyEnabled;

        float depth00 = orthographicDepth(uv + displacement + outlineThickness * vec2(-texelSize.x, texelSize.y));
        float depth01 = orthographicDepth(uv + displacement + outlineThickness * vec2(-texelSize.x, 0.0));
        float depth02 = orthographicDepth(uv + displacement + outlineThickness * vec2(-texelSize.x, -texelSize.y));

        float depth10 = orthographicDepth(uv + displacement + outlineThickness * vec2(0.0, -texelSize.y));
        float depth11 = orthographicDepth(uv + displacement + outlineThickness * vec2(0.0, 0.0));
        float depth12 = orthographicDepth(uv + displacement + outlineThickness * vec2(0.0, texelSize.y));

        float depth20 = orthographicDepth(uv + displacement + outlineThickness * vec2(texelSize.x, -texelSize.y));
        float depth21 = orthographicDepth(uv + displacement + outlineThickness * vec2(texelSize.x, 0.0));
        float depth22 = orthographicDepth(uv + displacement + outlineThickness * vec2(texelSize.x, texelSize.y));

        float xSobelDepth = Sx[0][0] * depth00 + Sx[1][0] * depth01 + Sx[2][0] * depth02 +
            Sx[0][1] * depth10 + Sx[1][1] * depth11 + Sx[2][1] * depth12 +
            Sx[0][2] * depth20 + Sx[1][2] * depth21 + Sx[2][2] * depth22;

        float ySobelDepth = Sy[0][0] * depth00 + Sy[1][0] * depth01 + Sy[2][0] * depth02 +
            Sy[0][1] * depth10 + Sy[1][1] * depth11 + Sy[2][1] * depth12 +
            Sy[0][2] * depth20 + Sy[1][2] * depth21 + Sy[2][2] * depth22;

        float gradientDepth = sqrt(pow(xSobelDepth, 2.0) + pow(ySobelDepth, 2.0));

        float normal00 = luminance(texture2D(normalBuffer, uv + displacement + outlineThickness * vec2(-texelSize.x, texelSize.y)).rgb);
        float normal01 = luminance(texture2D(normalBuffer, uv + displacement + outlineThickness * vec2(-texelSize.x, 0.0)).rgb);
        float normal02 = luminance(texture2D(normalBuffer, uv + displacement + outlineThickness * vec2(-texelSize.x, -texelSize.y)).rgb);

        float normal10 = luminance(texture2D(normalBuffer, uv + displacement + outlineThickness * vec2(0.0, -texelSize.y)).rgb);
        float normal11 = luminance(texture2D(normalBuffer, uv + displacement + outlineThickness * vec2(0.0, 0.0)).rgb);
        float normal12 = luminance(texture2D(normalBuffer, uv + displacement + outlineThickness * vec2(0.0, texelSize.y)).rgb);

        float normal20 = luminance(texture2D(normalBuffer, uv + displacement + outlineThickness * vec2(texelSize.x, -texelSize.y)).rgb);
        float normal21 = luminance(texture2D(normalBuffer, uv + displacement + outlineThickness * vec2(texelSize.x, 0.0)).rgb);
        float normal22 = luminance(texture2D(normalBuffer, uv + displacement + outlineThickness * vec2(texelSize.x, texelSize.y)).rgb);

        float xSobelNormal =
            Sx[0][0] * normal00 + Sx[1][0] * normal10 + Sx[2][0] * normal20 +
            Sx[0][1] * normal01 + Sx[1][1] * normal11 + Sx[2][1] * normal21 +
            Sx[0][2] * normal02 + Sx[1][2] * normal12 + Sx[2][2] * normal22;

        float ySobelNormal =
            Sy[0][0] * normal00 + Sy[1][0] * normal10 + Sy[2][0] * normal20 +
            Sy[0][1] * normal01 + Sy[1][1] * normal11 + Sy[2][1] * normal21 +
            Sy[0][2] * normal02 + Sy[1][2] * normal12 + Sy[2][2] * normal22;

        float gradientNormal = sqrt(pow(xSobelNormal, 2.0) + pow(ySobelNormal, 2.0));
        
        float luma00 = luminance(texture2D(inputBuffer, uv + displacement + outlineThickness * vec2(-texelSize.x, texelSize.y)).rgb);
        float luma01 = luminance(texture2D(inputBuffer, uv + displacement + outlineThickness * vec2(-texelSize.x, 0.0)).rgb);
        float luma02 = luminance(texture2D(inputBuffer, uv + displacement + outlineThickness * vec2(-texelSize.x, -texelSize.y)).rgb);
        float luma10 = luminance(texture2D(inputBuffer, uv + displacement + outlineThickness * vec2(0.0, -texelSize.y)).rgb);
        float luma11 = luminance(texture2D(inputBuffer, uv + displacement + outlineThickness * vec2(0.0, 0.0)).rgb);
        float luma12 = luminance(texture2D(inputBuffer, uv + displacement + outlineThickness * vec2(0.0, texelSize.y)).rgb);
        float luma20 = luminance(texture2D(inputBuffer, uv + displacement + outlineThickness * vec2(texelSize.x, -texelSize.y)).rgb);
        float luma21 = luminance(texture2D(inputBuffer, uv + displacement + outlineThickness * vec2(texelSize.x, 0.0)).rgb);
        float luma22 = luminance(texture2D(inputBuffer, uv + displacement + outlineThickness * vec2(texelSize.x, texelSize.y)).rgb);
        float xSobelLuma =
            Sx[0][0] * luma00 + Sx[1][0] * luma10 + Sx[2][0] * luma20 +
            Sx[0][1] * luma01 + Sx[1][1] * luma11 + Sx[2][1] * luma21 +
            Sx[0][2] * luma02 + Sx[1][2] * luma12 + Sx[2][2] * luma22;
        
        float ySobelLuma =
            Sy[0][0] * luma00 + Sy[1][0] * luma10 + Sy[2][0] * luma20 +
            Sy[0][1] * luma01 + Sy[1][1] * luma11 + Sy[2][1] * luma21 +
            Sy[0][2] * luma02 + Sy[1][2] * luma12 + Sy[2][2] * luma22;
        
        float gradientLuma = sqrt(pow(xSobelLuma, 2.0) + pow(ySobelLuma, 2.0));

        vec4 depthColor = vec4(vec3(orthographicDepth(uv)).rgb, 1.0);
        vec4 normalColor = vec4(texture2D(normalBuffer, uv).rgb, 1.0);
        vec4 materialLumaColor = vec4(vec3(luminance(inputColor.rgb)).rgb, 1.0);
        vec4 baseColor = inputColor*showMaterial + normalColor*showNormalMap + depthColor*showDepthMap + materialLumaColor*showMaterialLuma;

        if (showMaterial == 0.0 && showNormalMap == 0.0 && showDepthMap == 0.0 && showMaterialLuma == 0.0) {
          baseColor = vec4(0.0, 0.0, 0.0, 0.0);
        }

        float outline = outlineEnabled * (gradientNormal*normalCoefficient + gradientDepth*depthCoefficient + gradientLuma*lumaCoefficient);

        float threshold = 1.0;

        bool hideBackground = false;

        if (outline > threshold) {
            outputColor = vec4(0.0, 0.0, 0.0, 1.0);
            
            // if (gradientNormal*normalCoefficient > threshold) {
            //     outputColor.r = 1.0;
            // }
            // if (gradientDepth*depthCoefficient > threshold) {
            //     outputColor.g = 1.0;
            // }
            // if (gradientLuma*lumaCoefficient > threshold) {
            //     outputColor.b = 1.0;
            // }
        } else if (hideBackground && abs(depth) >= 1.0) { // is this always true? or just this model?
            // outputColor = baseColor;
            outputColor = vec4(1.0, 1.0, 1.0, 0.0); // good for outline renders
        } else {
            outputColor = baseColor;
        }
    
        
        // vec4 lineColor = vec4(outlineColor.rgb, 1.0);
        // vec4 color = mix(baseColor, lineColor, outline);
        // outputColor = color;

        

        // vec3 normalOutline = vec3(outlineEnabled * gradientNormal * normalCoefficient, 0, 0);
        // vec3 depthOutline = vec3(0, outlineEnabled *gradientDepth*depthCoefficient, 0);
        // vec3 lumaOutline = vec3(0, 0, outlineEnabled *gradientLuma*lumaCoefficient);
        // vec4 color = mix(baseColor, vec4(1.0, 1.0, 1.0, 1.0), vec4(outlineEnabled *gradientNormal*normalCoefficient, outlineEnabled *gradientDepth*depthCoefficient, outlineEnabled *gradientLuma*lumaCoefficient, 1.0));

        // outputColor = inputColor; // Passthrough
    }
    `;

    super("SuperOutlineEffect", fragmentShader, {
      blendFunction,
      attributes: EffectAttribute.CONVOLUTION | EffectAttribute.DEPTH,
      uniforms: new Map([
        ["normalBuffer", new Uniform(null)],
        ["showMaterial", new Uniform(showMaterial)],
        ["showMaterialLuma", new Uniform(showMaterialLuma)],
        ["showNormalMap", new Uniform(showNormalMap)],
        ["showDepthMap", new Uniform(showDepthMap)],
        ["outlineEnabled", new Uniform(outlineEnabled)],
        ["outlineThickness", new Uniform(outlineThickness)],
        ["outlineColor", new Uniform(outlineColor)],
        ["depthCoefficient", new Uniform(depthCoefficient)],
        ["normalCoefficient", new Uniform(normalCoefficient)],
        ["lumaCoefficient", new Uniform(lumaCoefficient)],
        ["shakyAmplitude", new Uniform(shakyAmplitude)],
        ["shakyFrequency", new Uniform(shakyFrequency)],
        ["shakyEnabled", new Uniform(shakyEnabled)],
      ]),
    });
  }
}

class SuperOutlineEffectComponent extends HTMLElement {
  static get observedAttributes() {
    return [
      "model-colors",
      "outline-thickness",
      "outline-color",
      "outline-enabled",
      "depth-coefficient",
      "normal-coefficient",
      "luma-coefficient",
      "shaky-amplitude",
      "shaky-frequency",
      "shaky-enabled",
    ];
  }

  constructor() {
    super();

    this.effects = [new SuperOutlineEffect()];
    this.effects[0].requireNormals = true;
    this.disabled = false;
  }

  updateNormalBuffer() {
    // console.log("trying to set normal buffer");
    if (this.effectComposer.normalBuffer) {
      this.effects[0].uniforms.get("normalBuffer").value =
        this.effectComposer.normalBuffer;
      // console.log("ok");
    } else {
      console.log("no normal buffer yet");
      setTimeout(this.updateNormalBuffer.bind(this), 100);
    }
  }

  connectedCallback() {
    super.connectedCallback && super.connectedCallback();

    if (this.parentNode?.nodeName.toLowerCase() === "effect-composer") {
      this.effectComposer = this.parentNode;
    }

    if (!this.effectComposer) {
      throw new Error(
        "<super-outline-effect> must be a descendent of an <effect-composer> component.",
      );
    }

    this.updateNormalBuffer();

    try {
      this.effectComposer.updateEffects();
    } catch (e) {
      // debugger;
      // console.error(e);
    }

    this;
  }

  disconnectedCallback() {
    super.disconnectedCallback && super.disconnectedCallback();
    this.effects.forEach((effect) => effect.dispose());
    this.effectComposer?.updateEffects();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.updateUniformsFromAttributes();
    if (this.effectComposer?.normalBuffer) {
      this.effects[0].uniforms.get("normalBuffer").value =
        this.effectComposer.normalBuffer;
    }

    if (this.effectComposer) {
      try {
        this.effectComposer.queueRender();
      } catch (e) {
        // debugger;
        // console.error(e);
      }
    }
  }

  updateUniformsFromAttributes() {
    if (this.getAttribute("model-colors") == "normals") {
      this.effects[0].uniforms.get("showMaterial").value = 0.0;
      this.effects[0].uniforms.get("showNormalMap").value = 1.0;
      this.effects[0].uniforms.get("showDepthMap").value = 0.0;
      this.effects[0].uniforms.get("showMaterialLuma").value = 0.0;
    } else if (this.getAttribute("model-colors") == "depth") {
      this.effects[0].uniforms.get("showMaterial").value = 0.0;
      this.effects[0].uniforms.get("showNormalMap").value = 0.0;
      this.effects[0].uniforms.get("showDepthMap").value = 1.0;
      this.effects[0].uniforms.get("showMaterialLuma").value = 0.0;
    } else if (this.getAttribute("model-colors") == "material") {
      this.effects[0].uniforms.get("showMaterial").value = 1.0;
      this.effects[0].uniforms.get("showNormalMap").value = 0.0;
      this.effects[0].uniforms.get("showDepthMap").value = 0.0;
      this.effects[0].uniforms.get("showMaterialLuma").value = 0.0;
    } else if (this.getAttribute("model-colors") == "luma") {
      this.effects[0].uniforms.get("showMaterial").value = 0.0;
      this.effects[0].uniforms.get("showNormalMap").value = 0.0;
      this.effects[0].uniforms.get("showDepthMap").value = 0.0;
      this.effects[0].uniforms.get("showMaterialLuma").value = 1.0;
    } else if (this.getAttribute("model-colors") == "none") {
      this.effects[0].uniforms.get("showMaterial").value = 0.0;
      this.effects[0].uniforms.get("showNormalMap").value = 0.0;
      this.effects[0].uniforms.get("showDepthMap").value = 0.0;
      this.effects[0].uniforms.get("showMaterialLuma").value = 0.0;
    } else {
      console.error(
        `super-outline-effect: invalid model-colors attribute value "${this.getAttribute("model-colors")}"`,
      );
    }

    this.effects[0].uniforms.get("outlineEnabled").value =
      this.getAttribute("outline-enabled") === "true" ? 1.0 : 0.0;


    const outlineThickness = parseFloat(this.getAttribute("outline-thickness"));
    this.effects[0].uniforms.get("outlineThickness").value = !isNaN(
      outlineThickness,
    )
      ? outlineThickness
      : 1.0;

    const depthCoefficient = parseFloat(this.getAttribute("depth-coefficient"));
    this.effects[0].uniforms.get("depthCoefficient").value = !isNaN(
      depthCoefficient,
    )
      ? depthCoefficient
      : 10.0;

    const normalCoefficient = parseFloat(
      this.getAttribute("normal-coefficient"),
    );
    this.effects[0].uniforms.get("normalCoefficient").value = !isNaN(
      normalCoefficient,
    )
      ? normalCoefficient
      : 10.0;

    const lumaCoefficient = parseFloat(this.getAttribute("luma-coefficient"));
    this.effects[0].uniforms.get("lumaCoefficient").value = !isNaN(
      lumaCoefficient,
    )
      ? lumaCoefficient
      : 2.5;

    const shakyAmplitude = parseFloat(this.getAttribute("shaky-amplitude"));
    this.effects[0].uniforms.get("shakyAmplitude").value = !isNaN(
      shakyAmplitude,
    )
      ? shakyAmplitude
      : 2.0;

    const shakyFrequency = parseFloat(this.getAttribute("shaky-frequency"));
    this.effects[0].uniforms.get("shakyFrequency").value = !isNaN(
      shakyFrequency,
    )
      ? shakyFrequency
      : 0.08;

    this.effects[0].uniforms.get("shakyEnabled").value =
      this.getAttribute("shaky-enabled") === "true" ? true : false;

    this.effects[0].uniforms.get("outlineColor").value = new Color(
      this.getAttribute("outline-color") || "black",
    );
  }
}

customElements.define("super-outline-effect", SuperOutlineEffectComponent);
